import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Space, Input, Row, Col, Divider, message, Spin } from 'antd';
import { CreditCardOutlined, LockOutlined, DollarOutlined, CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { PLAN_DETAILS } from '../constants';
import { THEME_COLORS, COLORS, GRADIENTS } from '../../../utils/colors';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
/** Create Account (Billing) submits to this endpoint. */
const ONBOARD_ENDPOINT = '/dealership/onboard';

const { Title, Text } = Typography;

// Initialize Stripe Test
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51Rh2DcR653i23Tab1smic23eQprVh2MmimrTJFsscCUSFnErz95rjklCsEfkmVery3PIiJezxMPenHS6Ib7tNfgd00NWPiaHDc';
const stripePromise = loadStripe(stripeKey);

// Stripe Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: THEME_COLORS.DARK.text.primary,
      fontFamily: 'Inter, system-ui, sans-serif',
      backgroundColor: 'transparent',
      '::placeholder': {
        color: THEME_COLORS.DARK.text.muted,
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

// Payment Form Component using Stripe Elements
const PaymentForm = ({ onNext, onBack, formData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getToken } = useAuth();
  const history = useHistory();
  const [billingInfo, setBillingInfo] = useState(formData.billingInfo || {
    billingName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState('');
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [promoExpanded, setPromoExpanded] = useState(false);

  // Date 7 days from now (when trial ends and $249 is due)
  const getTrialEndDate = () => {
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + 7);
    return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  useEffect(() => {
    if (stripe && elements) {
      setStripeLoaded(true);
    }
  }, [stripe, elements]);

  const handleInputChange = (field, value) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Prevent double submission
    if (isProcessing) {
      return;
    }
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setCardError('');

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingInfo.billingName,
          address: {
            line1: billingInfo.billingAddress,
            city: billingInfo.billingCity,
            state: billingInfo.billingState,
            postal_code: billingInfo.billingZip,
          },
        },
      });

      if (error) {
        setCardError(error.message);
        // Handle specific Stripe error about raw card data
        let errorMessage = error.message;
        if (error.message && error.message.includes('raw card data')) {
          errorMessage = 'Please use a test card number. For test mode, use: 4242 4242 4242 4242 with any future expiry date and CVC.';
        }
        message.error(errorMessage);
      } else {
        // Save dealership details to backend
        try {
          
          // Ensure name is explicitly captured - it comes from "Dealership name" field
          const dealershipName = formData.name || formData.dealershipName || '';
          
            if (!dealershipName) {
              message.error('Dealership name is required. Please go back and fill it in.');
              return;
            }
            
            // Ensure phone number is explicitly captured - works for both mobile and desktop
            const phoneNumber = formData.phone_number || formData.phone || '';
          
          const dealershipData = {
            // From formData (Step 1 - Dealership Details)
            // Dealership Name from "Dealership name" field - REQUIRED
            name: dealershipName,
            marketcheck_id: formData.marketcheck_id,
            // Phone number from form - works for both mobile and desktop
            phone_number: phoneNumber,
            website: formData.website,
            address_line1: formData.address_line1,
            city: formData.city,
            state: formData.state,
            zipcode: formData.zipcode,
            market_radius_miles: formData.market_radius_miles || 50,
            latitude: formData.latitude,
            longitude: formData.longitude,
            
            
            // From billingInfo (Step 3 - Billing)
            billingInfo: {
              billingName: billingInfo.billingName,
              billingAddress: billingInfo.billingAddress,
              billingCity: billingInfo.billingCity,
              billingState: billingInfo.billingState,
              billingZip: billingInfo.billingZip
            },
            
            // From payment processing (Step 3 - Stripe)
            paymentMethod: paymentMethod.id,
            
            // Subscription details
            subscription: {
              plan: 'early_access',
              price: PLAN_DETAILS.price,
              period: PLAN_DETAILS.period,
              trialDays: 7,
              ...(appliedCoupon ? { coupon_code: appliedCoupon } : {})
            }
          };
          

          // Get authentication token
          const token = await getToken({ template: 'default' });
          
          const response = await fetch(`${BASE_URL}/dealership/onboard`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(dealershipData)
          });

          if (response.ok) {
            const headerTaskId = response.headers.get('x-task-id') || 
                                 response.headers.get('task-id') ||
                                 response.headers.get('X-Task-Id');
            const responseData = await response.json();
            const bodyTaskId = responseData.fast_task_id ||
                              responseData.task_id || 
                              responseData.taskId || 
                              responseData.data?.task_id ||
                              responseData.data?.fast_task_id ||
                              responseData.result?.task_id ||
                              responseData.result?.fast_task_id ||
                              (responseData.task && responseData.task.id) ||
                              (responseData.task && responseData.task.task_id) ||
                              (responseData.task && responseData.task.fast_task_id);
            const taskId = headerTaskId || bodyTaskId;
            const taskIdValid = taskId && (typeof taskId === 'string' ? taskId.trim() !== '' : true);
            // Go directly to finish step; FinishStep will show "We're building your account" and poll task
            onNext({ ...dealershipData, task_id: taskIdValid ? taskId : undefined, apiResponse: responseData });
            return;
          } else {
            // Try to extract error message from response
            let errorMessage = 'Failed to save dealership details. Please try again.';
            try {
              // Read response as text first, then try to parse as JSON
              const responseText = await response.text();
              if (responseText) {
                try {
                  const errorData = JSON.parse(responseText);
                  if (errorData.detail) {
                    errorMessage = errorData.detail;
                  } else if (errorData.message) {
                    errorMessage = errorData.message;
                  } else if (errorData.error) {
                    errorMessage = errorData.error;
                  } else {
                    errorMessage = responseText;
                  }
                } catch (jsonError) {
                  // If not JSON, use the text as error message
                  errorMessage = responseText;
                }
              }
            } catch (parseError) {
              console.error('Failed to read error response:', parseError);
            }
            console.error('Dealership onboarding failed:', errorMessage);
            message.error(errorMessage);
          }
        } catch (error) {
          console.error('Dealership onboarding error:', error);
          // Extract error message from error object
          let errorMessage = 'Failed to save dealership details. Please try again.';
          if (error.message) {
            errorMessage = error.message;
          }
          message.error(errorMessage);
        }
      }
    } catch (error) {
      message.error('Failed to process payment. Please try again.');
      setCardError('An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid = billingInfo.billingName &&
                     billingInfo.billingAddress &&
                     billingInfo.billingCity &&
                     billingInfo.billingState &&
                     billingInfo.billingZip;

  return (
    <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <div style={{ marginBottom: 20, textAlign: 'center' }}>
          <Title level={3} style={{ color: THEME_COLORS.DARK.text.primary, marginBottom: 8 }}>
            Activate Your Hooptie Advantage
          </Title>
          <Text style={{ color: THEME_COLORS.DARK.text.secondary }}>
            Add your payment method to activate your Hooptie subscription.
          </Text>
        </div>

        <Row gutter={[20, 20]}>
          <Col xs={24} lg={14}>
            <Card 
              title={<Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 600 }}>Payment Information</Text>}
              style={{ 
                marginBottom: 16,
                background: THEME_COLORS.DARK.bg.secondary,
                border: `1px solid ${THEME_COLORS.DARK.border.secondary}`,
                color: THEME_COLORS.DARK.text.primary,
                overflow: 'visible'
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>
                    <CreditCardOutlined style={{ marginRight: 8, color: COLORS.PRIMARY[500] }} />
                    Card Information
                  </Text>
                  <div style={{
                    background: THEME_COLORS.DARK.bg.tertiary,
                    border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                    borderRadius: '8px',
                    padding: '12px',
                    minHeight: '44px',
                    opacity: stripeLoaded ? 1 : 0.6,
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {stripeLoaded ? (
                      <CardElement 
                        options={cardElementOptions}
                      />
                    ) : (
                      <div style={{ 
                        color: THEME_COLORS.DARK.text.muted, 
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: `2px solid ${THEME_COLORS.DARK.text.muted}`,
                          borderTop: `2px solid ${COLORS.PRIMARY[500]}`,
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Loading Stripe...
                      </div>
                    )}
                  </div>
                  {cardError && (
                    <Text style={{ color: '#fa755a', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {cardError}
                    </Text>
                  )}
                </div>

                <div>
                  <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>Cardholder Name</Text>
                  <Input
                    placeholder="John Doe"
                    value={billingInfo.billingName}
                    onChange={(e) => handleInputChange('billingName', e.target.value)}
                    style={{
                      background: THEME_COLORS.DARK.bg.tertiary,
                      border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                      color: THEME_COLORS.DARK.text.primary,
                      borderRadius: '8px',
                      height: '44px'
                    }}
                  />
                </div>
              </Space>
            </Card>

            <Card 
              title={<Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 600 }}>Billing Address</Text>}
              style={{ 
                background: THEME_COLORS.DARK.bg.secondary,
                border: `1px solid ${THEME_COLORS.DARK.border.secondary}`,
                color: THEME_COLORS.DARK.text.primary
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>Address</Text>
                  <Input
                    placeholder="123 Main Street"
                    value={billingInfo.billingAddress}
                    onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                    style={{
                      background: THEME_COLORS.DARK.bg.tertiary,
                      border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                      color: THEME_COLORS.DARK.text.primary,
                      borderRadius: '8px',
                      height: '44px'
                    }}
                  />
                </div>

                <Row gutter={12}>
                  <Col xs={12}>
                    <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>City</Text>
                    <Input
                      placeholder="New York"
                      value={billingInfo.billingCity}
                      onChange={(e) => handleInputChange('billingCity', e.target.value)}
                      style={{
                        background: THEME_COLORS.DARK.bg.tertiary,
                        border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                        color: THEME_COLORS.DARK.text.primary,
                        borderRadius: '8px',
                        height: '44px'
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>State</Text>
                    <Input
                      placeholder="NY"
                      value={billingInfo.billingState}
                      onChange={(e) => handleInputChange('billingState', e.target.value)}
                      maxLength={2}
                      style={{
                        background: THEME_COLORS.DARK.bg.tertiary,
                        border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                        color: THEME_COLORS.DARK.text.primary,
                        borderRadius: '8px',
                        height: '44px'
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, display: 'block', marginBottom: '8px' }}>ZIP</Text>
                    <Input
                      placeholder="10001"
                      value={billingInfo.billingZip}
                      onChange={(e) => handleInputChange('billingZip', e.target.value)}
                      maxLength={10}
                      style={{
                        background: THEME_COLORS.DARK.bg.tertiary,
                        border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
                        color: THEME_COLORS.DARK.text.primary,
                        borderRadius: '8px',
                        height: '44px'
                      }}
                    />
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card 
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.PRIMARY[500]}20 0%, ${COLORS.PRIMARY[500]}10 100%)`,
                border: `1px solid ${COLORS.PRIMARY[500]}30`,
                color: THEME_COLORS.DARK.text.primary,
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)'
              }}
            >
              <Title level={4} style={{ marginBottom: 20, color: THEME_COLORS.DARK.text.primary }}>
                <DollarOutlined style={{ marginRight: 8, color: COLORS.PRIMARY[500] }} />
                Order Summary
              </Title>

              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{ color: THEME_COLORS.DARK.text.primary }}>Hooptie Elite</Text>
                  <Text strong style={{ color: THEME_COLORS.DARK.text.primary }}>$249/{PLAN_DETAILS.period}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{ color: THEME_COLORS.DARK.text.muted }}>Dealership: {formData?.name || '-'}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{ color: THEME_COLORS.DARK.text.muted }}>Market: {formData?.zipcode || '-'}</Text>
                </div>
                
                {/* Create Account: POST to /dealership/onboard */}
                <Button
                  type="submit"
                  htmlType="submit"
                  disabled={!isFormValid || !stripeLoaded}
                  loading={isProcessing}
                  block
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    margin: '12px 0',
                    height: 'auto',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)'
                  }}
                >
                  {isProcessing ? 'Creating…' : 'Create Account'}
                </Button>

                {/* Promo code: below Create Account — "Have a promo code?" with collapsible Apply */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: appliedCoupon ? 0 : promoExpanded ? 12 : 0 }}>
                    <Text style={{ color: THEME_COLORS.DARK.text.primary, fontSize: 14 }}>
                      Have a promo code?
                    </Text>
                    {!promoExpanded && !appliedCoupon && (
                      <button
                        type="button"
                        onClick={() => { setPromoExpanded(true); setCouponError(null); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 14,
                          color: COLORS.PRIMARY[400],
                          fontFamily: 'inherit',
                        }}
                      >
                        Apply &gt;
                      </button>
                    )}
                  </div>
                  {(promoExpanded || appliedCoupon) && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 0,
                        borderRadius: 10,
                        border: `1px solid ${couponError ? 'rgba(248, 113, 113, 0.6)' : 'rgba(99, 102, 241, 0.5)'}`,
                        boxShadow: couponError ? '0 0 0 1px rgba(248, 113, 113, 0.2), 0 0 12px rgba(248, 113, 113, 0.15)' : '0 0 0 1px rgba(99, 102, 241, 0.15), 0 0 12px rgba(99, 102, 241, 0.12)',
                        overflow: 'hidden',
                        background: THEME_COLORS.DARK.bg.tertiary,
                      }}
                    >
                      <Input
                        placeholder="Enter promo code"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value);
                          setCouponError(null);
                        }}
                        disabled={!!appliedCoupon}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), document.querySelector('[data-promo-apply]')?.click())}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: 'transparent',
                          border: 'none',
                          color: THEME_COLORS.DARK.text.primary,
                          padding: '10px 14px',
                          fontSize: 14,
                          borderRadius: 0,
                        }}
                      />
                      <Button
                        type="button"
                        data-promo-apply
                        onClick={() => {
                          const code = couponCode.trim().toUpperCase();
                          if (!code) {
                            setCouponError('That code didn\'t work. Try another.');
                            return;
                          }
                          setCouponError(null);
                          setAppliedCoupon(code);
                          setCouponCode(code);
                          setPromoExpanded(true);
                          message.success('Promo applied');
                        }}
                        disabled={couponLoading || !!appliedCoupon}
                        style={{
                          margin: 0,
                          height: 'auto',
                          padding: '10px 18px',
                          border: 'none',
                          borderRadius: 0,
                          background: appliedCoupon
                            ? COLORS.SUCCESS[500]
                            : `linear-gradient(90deg, ${COLORS.PRIMARY[600]} 0%, ${COLORS.SECONDARY[500]} 100%)`,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 14,
                        }}
                      >
                        {appliedCoupon ? 'Applied' : 'Apply'}
                      </Button>
                    </div>
                  )}
                  {couponError && (
                    <Text style={{ fontSize: 12, color: '#f87171', display: 'block', marginTop: 8 }}>
                      That code didn&apos;t work. Try another.
                    </Text>
                  )}
                  {appliedCoupon && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <Text style={{ fontSize: 12, color: COLORS.SUCCESS[500] }}>
                        Promo <strong>{appliedCoupon}</strong> will be applied at checkout.
                      </Text>
                      <button
                        type="button"
                        onClick={() => { setAppliedCoupon(null); setCouponCode(''); setCouponError(null); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 12,
                          color: THEME_COLORS.DARK.text.muted,
                          fontFamily: 'inherit',
                          textDecoration: 'underline',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                
                <Divider style={{ borderColor: THEME_COLORS.DARK.border.primary }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text strong style={{ color: THEME_COLORS.DARK.text.primary }}>Due today</Text>
                  <Text strong style={{ fontSize: 18, color: COLORS.SUCCESS[500] }}>$0</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{ color: THEME_COLORS.DARK.text.primary }}>Due {getTrialEndDate()}</Text>
                  <Text strong style={{ fontSize: 16, color: THEME_COLORS.DARK.text.primary }}>$249</Text>
                </div>
                <Text style={{ color: THEME_COLORS.DARK.text.muted, fontSize: 13, display: 'block', marginBottom: 8 }}>
                  Cancel before {getTrialEndDate()} to avoid being charged.
                </Text>
              </div>

              <div style={{ 
                background: `rgba(${COLORS.SUCCESS[500].replace('#', '')}, 0.1)`, 
                padding: 12, 
                borderRadius: 8,
                marginBottom: 16,
                textAlign: 'center',
                border: `1px solid ${COLORS.SUCCESS[500]}20`
              }}>
                <CheckCircleOutlined style={{ color: COLORS.SUCCESS[500], marginRight: 8 }} />
                <Text strong style={{ color: COLORS.SUCCESS[500], fontSize: '13px' }}>
                  No contract • Cancel anytime
                </Text>
              </div>

              <div style={{ 
                background: `rgba(${COLORS.PRIMARY[500].replace('#', '')}, 0.05)`, 
                padding: 12, 
                borderRadius: 8,
                textAlign: 'center',
                border: `1px solid ${COLORS.PRIMARY[500]}20`
              }}>
                <LockOutlined style={{ color: COLORS.PRIMARY[500], marginRight: 8 }} />
                <Text style={{ color: THEME_COLORS.DARK.text.muted, fontSize: 11 }}>
                  Your payment information is encrypted and secure. We use industry-standard security practices.
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </form>
  );
};

// Main BillingStep component that wraps PaymentForm with Stripe Elements
const BillingStep = ({ onNext, onBack, formData }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm onNext={onNext} onBack={onBack} formData={formData} />
    </Elements>
  );
};

export default BillingStep;
